import { useState, useEffect, useMemo, useContext } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import _pick from 'lodash/pick'
import FilterDropdownWrapper from 'common/FilterDropdownWrapper'
import { isArray } from 'lodash'
import { modifyURLQuery } from 'utils/common'
import { MobileFilterModalContext } from 'context/ProviderSearch/MobileFilterModalProvider'
import useBreakpoint from 'hooks/useBreakPoint'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
const MultiselectDropdown = dynamic(() => import('common/MultiselectDropdown'))

type PillMultiselectDropdownFilterProps = {
  filterList: ProviderFilter[]
  // This prop is used when applying filter
  filterKey: string
  placeholder: string
  // This prop is used to fill the placeholder text for search input and title
  filterTypeText: string
  filterType: ProviderFilterTypes
  showListHeaders?: boolean
  hideFilterSearchInput?: boolean
}

const PillMultiselectDropdownFilter: React.FC<PillMultiselectDropdownFilterProps> = ({
  filterList,
  filterKey,
  placeholder,
  filterTypeText,
  filterType,
  showListHeaders,
  hideFilterSearchInput,
}) => {
  const router = useRouter()
  const { query } = router
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<string[]>([])
  const { openMobileFilter } = useContext(MobileFilterModalContext)
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  useEffect(() => {
    // This is how its present in the url query with [] for array items
    // eg:- availabilityType[]=fa040321-294c-4d0a-8c05...
    const queryFilterKey = `${filterKey}[]`
    const idsInQuery = _pick(query, queryFilterKey)[queryFilterKey]

    if (!idsInQuery && !selectedFilters.length) {
      return
    }

    if (!idsInQuery) {
      //When Clear button in pill was clicked
      setSelectedFilters([])
      return
    }

    if (!isArray(idsInQuery)) {
      //In case only one filer option was selected
      setSelectedFilters([idsInQuery as string])
    } else {
      setSelectedFilters([...(idsInQuery as string[])])
    }
  }, [query])

  const onApply = (selectedFilters: string[]) => {
    setSelectedFilters(selectedFilters)
    modifyURLQuery(router, { [filterKey]: selectedFilters, offset: '0' })
  }

  const clearSelectedValue = () => {
    onApply([])
  }

  const filterStringValue = useMemo(() => {
    return selectedFilters.reduce((concat, filterId, index) => {
      const filter = filterList.find((filter) => filter.id === filterId)

      if (filter) {
        if (index === 0) {
          return filter.value
        }

        return `${concat}, ${filter.value}`
      }

      return concat
    }, '')
  }, [selectedFilters])

  return (
    <FilterDropdownWrapper
      placeholder={placeholder}
      value={filterStringValue}
      onPillClick={(shouldShow) => {
        if (isMobileScreen || isLargeSize) {
          openMobileFilter(filterType)
        } else {
          setShowDropdown(shouldShow)
        }
      }}
      showDropdown={showDropdown}
      onClearValue={clearSelectedValue}
      className="mr-2"
    >
      {showDropdown && (
        <MultiselectDropdown
          filterTypeText={filterTypeText}
          filterList={filterList}
          appliedFilters={selectedFilters}
          onApply={onApply}
          showListHeaders={showListHeaders}
          hideFilterSearchInput={hideFilterSearchInput}
        />
      )}
    </FilterDropdownWrapper>
  )
}

export default PillMultiselectDropdownFilter
